// LLM generated - Model Claude 4 Sonnet
import { ButtonProps, Menu , useMantineTheme} from '@mantine/core';
import { IconMoodPlus } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useState, forwardRef, useImperativeHandle } from 'react';

import { Button } from '../../../../Button';
import { Text } from '../../../../Text';
import type { Reaction } from '~/services/live-activity/types';
import { useStyles } from './ReactionDropdown.styles';

type ReactionDropdownProps = {
  availableReactions: Reaction[];
  onReactionSelect: (reactionCode: string) => void;
  onReactionRemove: (reactionCode: string) => void;
  userReactions?: string[];
  isLoading?: boolean;
  disabled?: boolean;
  reactions?: string[];
  reactionCount?: number;
  variant?: 'other' | 'current';
};

export type ReactionDropdownRef = {
  openDropdown: () => void;
};

const ReactionDropdown = forwardRef<ReactionDropdownRef, ReactionDropdownProps>(
  (
    {
      availableReactions,
      onReactionSelect,
      onReactionRemove,
      userReactions = [],
      isLoading = false,
      disabled = false,
      reactions = [],
      reactionCount = 0,
      variant = 'other',
    },
    ref
  ) => {
    const { t } = useTranslation('chat');
    const { classes, theme } = useStyles({ variant });
    const [opened, setOpened] = useState(false);

    useImperativeHandle(ref, () => ({
      openDropdown: () => setOpened(true),
    }));

    const handleReactionClick = (reactionCode: string) => {
      const hasUserReacted = userReactions.includes(reactionCode);

      if (hasUserReacted) {
        // User has already reacted with this emoji, so remove it
        onReactionRemove(reactionCode);
      } else {
        // User hasn't reacted with this emoji, so add it
        onReactionSelect(reactionCode);
      }

      // Close the dropdown after reaction is sent
      setOpened(false);
    };

    return (
      <Menu
        shadow="md"
        disabled={disabled}
        position="bottom-start"
        offset={variant === 'current' ? -90 : -60}
        opened={opened}
        onChange={setOpened}
        classNames={{
          dropdown: classes.menuDropdown,
        }}
      >
        <Menu.Target>
          <Button
            variant="text"
            disabled={isLoading || disabled}
            aria-label={t('ariaLabels.addReaction')}
            className={classes.leftAlignedButton}
          >
            {reactions.length > 0 ? (
              <div className={classes.reactionsDisplay}>
                {reactions.map((reactionCode) => {
                  const reactionData = availableReactions.find((r) => r.code === reactionCode);
                  return reactionData ? (
                    <img
                      key={reactionCode}
                      src={reactionData.url}
                      alt={reactionCode}
                      className={classes.reactionImage}
                    />
                  ) : (
                    <span key={reactionCode} className={classes.reactionText}>
                      {reactionCode}
                    </span>
                  );
                })}
                {reactionCount > 0 && (
                  <Text
                    variant="title-medium"
                    style={{
                      marginLeft: theme.other.spacing._4,
                      color: variant === 'current' ? theme.white : theme.colors.gray2[5],
                    }}
                  >
                    {reactionCount}
                  </Text>
                )}
              </div>
            ) : (
              <IconMoodPlus
                size={16}
                color={variant === 'current' ? theme.white : theme.colors.gray2[5]}
              />
            )}
          </Button>
        </Menu.Target>

        <Menu.Dropdown className={classes.dropdown}>
          <div className={classes.reactionsContainer}>
            {availableReactions.map((reaction) => (
              <button
                key={reaction.code}
                className={classes.reactionButton}
                onClick={() => handleReactionClick(reaction.code)}
                type="button"
                aria-label={t('ariaLabels.addReaction')}
              >
                <img src={reaction.url} alt={reaction.code} className={classes.reactionImage} />
              </button>
            ))}
          </div>
        </Menu.Dropdown>
      </Menu>
    );
  }
);

ReactionDropdown.displayName = 'ReactionDropdown';

export default ReactionDropdown;
